import type { Node } from '../../../../core/models/scene';
import { getTableCellRectByKey } from './tableDrop.utils';
import { getTableCellLayoutAlgorithm, resolveCellLayoutConfig } from './tableCellLayout.registry';

type TableDock = { tableId: string; cellKey: `${number},${number}`; offset?: { x: number; y: number } };

export const computeDockedLayoutUpdatesForTable = (tableNode: Node, allNodes: Node[]): Array<{ nodeId: string; changes: any }> => {
  const attrs = (tableNode.attrs ?? {}) as Record<string, unknown>;

  const docked = allNodes.filter((n) => {
    const dock = (n as any)?.attrs?.tableDock as TableDock | undefined;
    return !!dock && dock.tableId === tableNode.id;
  });
  if (docked.length === 0) return [];

  const groups = new Map<
    `${number},${number}`,
    {
      cellRect: { x: number; y: number; w: number; h: number };
      items: Array<{ id: string; w: number; h: number; x: number; y: number; dock: TableDock }>;
    }
  >();

  for (const n of docked) {
    const dock = (n as any)?.attrs?.tableDock as TableDock | undefined;
    if (!dock) continue;
    const hit = getTableCellRectByKey(tableNode, dock.cellKey);
    if (!hit) continue;
    const key = hit.cellKey;
    const w = ((n as any).w ?? (n as any).width ?? 60) as number;
    const h = ((n as any).h ?? (n as any).height ?? 60) as number;
    const item = { id: n.id, w: Number(w) || 60, h: Number(h) || 60, x: Number((n as any).x) || 0, y: Number((n as any).y) || 0, dock };
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(key, { cellRect: hit.contentRect, items: [item] });
    }
  }

  const updates: Array<{ nodeId: string; changes: any }> = [];
  groups.forEach((group, cellKey) => {
    const config = resolveCellLayoutConfig(attrs, cellKey as any);
    const algorithm = getTableCellLayoutAlgorithm(config.algorithmId);
    const layoutRect = group.cellRect;
    const itemsForAlgorithm =
      config.algorithmId === 'fixed'
        ? group.items.map((it) => {
            const offX =
              typeof it.dock?.offset?.x === 'number' && Number.isFinite(it.dock.offset.x)
                ? it.dock.offset.x
                : it.x - layoutRect.x;
            const offY =
              typeof it.dock?.offset?.y === 'number' && Number.isFinite(it.dock.offset.y)
                ? it.dock.offset.y
                : it.y - layoutRect.y;
            return { id: it.id, w: it.w, h: it.h, x: offX, y: offY };
          })
        : group.items.map((it) => ({ id: it.id, w: it.w, h: it.h, x: it.x, y: it.y }));

    const arranged = algorithm.arrange({ cellRect: layoutRect, items: itemsForAlgorithm, params: config.params });
    for (const it of group.items) {
      const next = arranged[it.id];
      if (!next) continue;
      if (config.algorithmId === 'fixed') {
        const nextOffset = { x: next.x - layoutRect.x, y: next.y - layoutRect.y };
        updates.push({
          nodeId: it.id,
          changes: {
            x: next.x,
            y: next.y,
            attrs: {
              tableDock: { ...it.dock, offset: nextOffset },
            },
          },
        });
      } else {
        updates.push({ nodeId: it.id, changes: { x: next.x, y: next.y } });
      }
    }
  });

  return updates;
};
